import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  filaEntregaService,
  FilaEntregaFilters,
  CreateFilaEntregaData,
  UpdateFilaEntregaData,
} from "../services/filaEntregaService";
import { useGlobalStore } from "../store/globalStore";
import { getErrorMessage } from "../utils/errorHandler";

export const useFilaEntregas = (
  filters?: FilaEntregaFilters,
  options?: {
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    /** Quando true (padrão), percorre todas as páginas. Use false na operação do dia. */
    fetchAllPages?: boolean;
  }
) => {
  const fetchAllPages = options?.fetchAllPages !== false;
  return useQuery({
    queryKey: ["filaEntregas", filters, fetchAllPages ? "all" : "page"],
    queryFn: async () => {
      if (!filters) return [];
      if (fetchAllPages) {
        return filaEntregaService.getFilaEntregas(filters);
      }
      const res = await filaEntregaService.getFilaEntregasPaginated(filters);
      return res.data;
    },
    enabled: filters != null,
    refetchInterval: options?.refetchInterval,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
  });
};

export const useFilaEntregaById = (id: number) => {
  return useQuery({
    queryKey: ["filaEntrega", id],
    queryFn: () => filaEntregaService.getFilaEntregaById(id),
    enabled: !!id,
  });
};

export const useCreateFilaEntrega = () => {
  const queryClient = useQueryClient();
  const { setError } = useGlobalStore();

  return useMutation({
    mutationFn: (data: CreateFilaEntregaData) =>
      filaEntregaService.createFilaEntrega(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["filaEntregas"] });
      queryClient.invalidateQueries({ queryKey: ["usuarios"] });
    },
    onError: (error) => setError(getErrorMessage(error)),
  });
};

export const useUpdateFilaEntrega = () => {
  const queryClient = useQueryClient();
  const { setError } = useGlobalStore();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateFilaEntregaData }) =>
      filaEntregaService.updateFilaEntrega(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["filaEntregas"] });
      queryClient.invalidateQueries({ queryKey: ["usuarios"] });
      queryClient.invalidateQueries({ queryKey: ["entregas"] });
    },
    onError: (error) => setError(getErrorMessage(error)),
  });
};

export const useDeleteFilaEntrega = () => {
  const queryClient = useQueryClient();
  const { setError } = useGlobalStore();

  return useMutation({
    mutationFn: (id: number) => filaEntregaService.deleteFilaEntrega(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["filaEntregas"] });
      queryClient.invalidateQueries({ queryKey: ["usuarios"] });
    },
    onError: (error) => setError(getErrorMessage(error)),
  });
};
